import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';
import { login, sendForgotPasswordCode, resetPassword } from '../../api';
import { getApiErrorMessage } from '../../utils/apiErrorUtils';
import './index.css';
import './index.tablet.css';
import './index.mobile.css';

const INFO_CIRCLE_ICON = `${process.env.PUBLIC_URL}/image/login_info_circle.svg`;
const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

// ─── Shared sub-components ───────────────────────────────────────────────────

function BackButton({ label, onClick }) {
  return (
    <button className="lgn-back-btn" onClick={onClick}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M10.7999 1.6665L4.46655 7.99984L10.7999 14.3332"
          stroke="#0F74EF"
          strokeWidth="1.33333"
          strokeLinejoin="round"
        />
      </svg>
      {label}
    </button>
  );
}

function PrimaryButton({ children, onClick, disabled }) {
  return (
    <button
      className={`lgn-btn-primary${disabled ? ' lgn-btn-primary--disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function FieldLabel({ required, children }) {
  return (
    <div className="lgn-field-label">
      {required && <span className="lgn-required">*</span>}
      {children}
    </div>
  );
}

function TextInput({ placeholder, value, onChange, type = 'text', error }) {
  return (
    <input
      type={type}
      className={`lgn-input${error ? ' lgn-input--error' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ErrorHint({ message }) {
  if (!message) return null;
  return (
    <div className="lgn-error-hint">
      <img src={INFO_CIRCLE_ICON} alt="" className="lgn-error-icon" />
      <span>{message}</span>
    </div>
  );
}

// ─── OTP Input ───────────────────────────────────────────────────────────────

function OtpInput({ value, onChange, hasError }) {
  const inputs = useRef([]);

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.split('');
    next[index] = char;
    const newVal = next.join('');
    onChange(newVal);
    if (char && index < OTP_LENGTH - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputs.current[index - 1]?.focus();
        const next = value.split('');
        next[index - 1] = '';
        onChange(next.join(''));
      } else {
        const next = value.split('');
        next[index] = '';
        onChange(next.join(''));
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    onChange(pasted.padEnd(OTP_LENGTH, '').slice(0, OTP_LENGTH));
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputs.current[focusIdx]?.focus();
  };

  return (
    <div className="lgn-otp-row">
      {Array.from({ length: OTP_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className={`lgn-otp-box${value[i] ? ' lgn-otp-box--filled' : ''}${hasError ? ' lgn-otp-box--error' : ''}`}
        />
      ))}
    </div>
  );
}

// ─── Password Strength Bar ────────────────────────────────────────────────────

function PasswordStrengthBar({ password }) {
  const getStrength = () => {
    if (!password) return 0;
    let score = 0;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };

  const strength = getStrength();
  // 1 bar = weak (red), 2 bars = medium (orange), 3 bars = strong (green)
  const levelColor = strength >= 3 ? '#069c67' : strength === 2 ? '#ff7700' : '#f54336';

  return (
    <div className="lgn-strength-bars">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="lgn-strength-bar"
          style={{ background: strength > i ? levelColor : '#d9d9d9' }}
        />
      ))}
    </div>
  );
}

// ─── View: Login Form ─────────────────────────────────────────────────────────

function LoginForm({ t, onForgotPassword, onLogin, error }) {
  const savedAccount = localStorage.getItem('rememberedAccount') || '';
  const [account, setAccount] = useState(savedAccount);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(!!savedAccount);

  return (
    <div className="lgn-card">
      <div className="lgn-card-header">
        <div className="lgn-logo-placeholder">
          <img src="/image/家庭友好企业Logo.png" alt="logo" />
        </div>

        <span className="lgn-center-name">{t('login.centerName')}</span>
      </div>
      <h1 className="lgn-welcome">{t('login.welcome')}</h1>
      <p className="lgn-subtitle">{t('login.subtitle')}</p>

      <div className="lgn-field">
        <FieldLabel>{t('login.accountLabel')}</FieldLabel>
        <TextInput
          placeholder={t('login.accountPlaceholder')}
          value={account}
          onChange={setAccount}
        />
      </div>

      <div className="lgn-field">
        <FieldLabel>{t('login.passwordLabel')}</FieldLabel>
        <TextInput
          type="password"
          placeholder={t('login.passwordPlaceholder')}
          value={password}
          onChange={setPassword}
        />
      </div>

      <div className="lgn-remember-row">
        <Checkbox
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="lgn-checkbox-antd"
        >
          <span className="lgn-checkbox-text">{t('login.rememberMe')}</span>
        </Checkbox>
        <button className="lgn-forgot-link" onClick={onForgotPassword}>
          {t('login.forgotPassword')}
        </button>
      </div>

      <PrimaryButton onClick={() => onLogin({ account, password, rememberMe })}>
        {t('login.submit')}
      </PrimaryButton>
      <ErrorHint message={error} />

      <p className="lgn-register-hint">
        <span>{t('login.noAccount')} </span>
        <Link to="/register" className="lgn-register-link">
          {t('login.register')}
        </Link>
      </p>
    </div>
  );
}

// ─── View: Forgot Password Step 1 (confirm account) ──────────────────────────

function ForgotStep1({ t, onNext, onBack, error }) {
  const [account, setAccount] = useState('');

  return (
    <div className="lgn-card lgn-card--forgot">
      <h2 className="lgn-forgot-title">{t('login.forgot.title')}</h2>
      <p className="lgn-forgot-subtitle">{t('login.forgot.subtitle')}</p>

      <div className="lgn-field">
        <FieldLabel required>{t('login.forgot.accountLabel')}</FieldLabel>
        <TextInput
          placeholder={t('login.forgot.accountPlaceholder')}
          value={account}
          onChange={setAccount}
          error={!!error}
        />
        {error && (
          <div className="lgn-not-activated-header">
            <img src={INFO_CIRCLE_ICON} alt="" className="lgn-error-icon" />
            <span className="lgn-not-activated-title">{error}</span>
          </div>
        )}
      </div>

      <PrimaryButton onClick={() => onNext(account)}>{t('login.forgot.next')}</PrimaryButton>

      <BackButton label={t('login.forgot.backToLogin')} onClick={onBack} />
    </div>
  );
}

// ─── View: Forgot Password — Account Not Activated ───────────────────────────

function ForgotNotActivated({ t, accountId, onResend, onBack }) {
  return (
    <div className="lgn-card lgn-card--forgot lgn-card--tall">
      <h2 className="lgn-forgot-title">{t('login.forgot.title')}</h2>
      <p className="lgn-forgot-subtitle">{t('login.forgot.subtitle')}</p>

      <div className="lgn-field">
        <FieldLabel required>{t('login.forgot.accountLabel')}</FieldLabel>
        <div className="lgn-input lgn-input--filled">{accountId}</div>
      </div>

      <div className="lgn-not-activated-box">
        <div className="lgn-not-activated-header">
          <img src={INFO_CIRCLE_ICON} alt="" className="lgn-error-icon" />
          <span className="lgn-not-activated-title">{t('login.forgot.notActivated.title')}</span>
        </div>
        <p className="lgn-not-activated-desc">
          {t('login.forgot.notActivated.desc1')} <strong>{accountId}</strong>{' '}
          {t('login.forgot.notActivated.desc2')}
        </p>
        <div className="lgn-not-activated-actions">
          <button className="lgn-btn-secondary lgn-btn-secondary--blue" onClick={onResend}>
            {t('login.forgot.notActivated.resend')}
          </button>
          <button className="lgn-btn-secondary" onClick={onBack}>
            {t('login.forgot.notActivated.backToLogin')}
          </button>
        </div>
      </div>

      <PrimaryButton disabled>{t('login.forgot.next')}</PrimaryButton>

      <BackButton label={t('login.forgot.backToLogin')} onClick={onBack} />
    </div>
  );
}

// ─── View: Reset Password Step 2 (OTP + new password) ────────────────────────

function ResetPasswordForm({
  t,
  maskedEmail,
  onSubmit,
  onBack,
  onResend,
  submitting,
  submitError,
}) {
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);
  const [errors, setErrors] = useState({});

  const startCountdown = useCallback(() => {
    setCountdown(RESEND_SECONDS);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCountdown();
    return () => clearInterval(countdownRef.current);
  }, [startCountdown]);

  const handleResend = () => {
    if (countdown > 0) return;
    startCountdown();
    onResend?.();
  };

  const validate = () => {
    const errs = {};
    if (otp.replace(/\D/g, '').length < OTP_LENGTH) errs.otp = 'required';
    if (password.length < 8 || password.length > 20) {
      errs.password = true;
    } else {
      const categories = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/];
      const met = categories.filter((re) => re.test(password)).length;
      if (met < 3) errs.password = true;
    }
    if (password !== confirmPassword) errs.confirmPassword = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  return (
    <div className="lgn-card lgn-card--forgot lgn-card--reset">
      <h2 className="lgn-forgot-title">{t('login.reset.title')}</h2>

      <div className="lgn-code-sent-banner">
        <span className="lgn-code-sent-label">{t('login.reset.codeSentTo')}</span>{' '}
        <strong className="lgn-code-sent-email">{maskedEmail}</strong>
      </div>

      <div className="lgn-field">
        <FieldLabel required>{t('login.reset.codeLabel')}</FieldLabel>
        <OtpInput value={otp} onChange={setOtp} hasError={!!errors.otp} />
        <div className="lgn-otp-footer">
          <span className="lgn-otp-valid">{t('login.reset.codeValid')}</span>
          <button
            className={`lgn-resend-btn${countdown > 0 ? ' lgn-resend-btn--disabled' : ''}`}
            onClick={handleResend}
            disabled={countdown > 0}
          >
            {countdown > 0
              ? t('login.reset.resendCountdown', { seconds: countdown })
              : t('login.reset.resendCode')}
          </button>
        </div>
        <span className="lgn-spam-hint">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00067 0.666748C12.0505 0.6671 15.3337 3.95086 15.3337 8.00073C15.3333 12.0503 12.0502 15.3334 8.00067 15.3337C3.9508 15.3337 0.667039 12.0505 0.666687 8.00073C0.666687 3.95064 3.95058 0.666748 8.00067 0.666748ZM8.00067 2.00073C4.68696 2.00073 2.00067 4.68702 2.00067 8.00073C2.00102 11.3141 4.68718 14.0007 8.00067 14.0007C11.3139 14.0004 14.0003 11.3139 14.0007 8.00073C14.0007 4.68724 11.3141 2.00108 8.00067 2.00073ZM8.66669 10.6667H9.66669V11.9998H6.33368V10.6667H7.33368V7.33374H6.66669V5.99976H8.66669V10.6667ZM8.00067 3.66675C8.4605 3.6671 8.8335 4.03989 8.83368 4.49976C8.83368 4.95978 8.46061 5.33339 8.00067 5.33374C7.54043 5.33374 7.16669 4.95999 7.16669 4.49976C7.16686 4.03967 7.54054 3.66675 8.00067 3.66675Z"
              fill="#AAAAAA"
            />
          </svg>
          {t('login.reset.spamHint')}
        </span>
        {errors.otp && <ErrorHint message={t('login.reset.codeError')} />}
      </div>

      <div className="lgn-field">
        <FieldLabel required>{t('login.reset.passwordLabel')}</FieldLabel>
        <TextInput
          type="password"
          placeholder={t('login.reset.passwordPlaceholder')}
          value={password}
          onChange={setPassword}
          error={errors.password}
        />
        <PasswordStrengthBar password={password} />
        <p className="lgn-hint">{t('login.reset.passwordHint')}</p>
      </div>

      <div className="lgn-field">
        <FieldLabel required>{t('login.reset.confirmLabel')}</FieldLabel>
        <TextInput
          type="password"
          placeholder={t('login.reset.confirmPlaceholder')}
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={errors.confirmPassword}
        />
      </div>

      <PrimaryButton
        disabled={submitting}
        onClick={() => {
          if (validate()) onSubmit({ otp, password });
        }}
      >
        {submitting ? t('login.reset.submitting') : t('login.reset.submit')}
      </PrimaryButton>
      {submitError && <ErrorHint message={submitError} />}

      <BackButton label={t('login.reset.back')} onClick={onBack} />
    </div>
  );
}

// ─── Page orchestrator ────────────────────────────────────────────────────────

// Views: 'login' | 'forgot' | 'notActivated' | 'reset'
function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const cloudBgUrl = `${process.env.PUBLIC_URL}/image/云朵背景.jpg`;

  const [view, setView] = useState('login');
  const [loginError, setLoginError] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotAccount, setForgotAccount] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleLogin = async ({ account, password, rememberMe }) => {
    setLoginError('');
    try {
      await login(account, password);
      if (rememberMe) {
        localStorage.setItem('rememberedAccount', account);
      } else {
        localStorage.removeItem('rememberedAccount');
      }
      navigate('/family-friendly/overview');
    } catch (err) {
      setLoginError(getApiErrorMessage(err.message, t, 'login.error.invalid'));
    }
  };

  const handleForgotNext = async (account) => {
    if (!account) return;
    setForgotAccount(account);
    setForgotError('');
    try {
      const res = await sendForgotPasswordCode(account, i18n.language);
      setMaskedEmail(res.result?.emailMasked || '');
      setView('reset');
    } catch (err) {
      setForgotError(getApiErrorMessage(err.message, t, 'login.forgot.accountNotFound'));
    }
  };

  const handleResetSubmit = async ({ otp, password }) => {
    setResetSubmitting(true);
    setResetError('');
    try {
      await resetPassword(forgotAccount, otp, password);
      setView('login');
    } catch (err) {
      setResetError(getApiErrorMessage(err.message, t, 'login.reset.error'));
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <div className="lgn-page">
      <div className="lgn-cloud-bg" style={{ backgroundImage: `url(${cloudBgUrl})` }} />

      <div className="lgn-center">
        {view === 'login' && (
          <LoginForm
            t={t}
            error={loginError}
            onForgotPassword={() => {
              setForgotError('');
              setView('forgot');
            }}
            onLogin={handleLogin}
          />
        )}

        {view === 'forgot' && (
          <ForgotStep1
            t={t}
            error={forgotError}
            onNext={handleForgotNext}
            onBack={() => setView('login')}
          />
        )}

        {view === 'notActivated' && (
          <ForgotNotActivated
            t={t}
            accountId={forgotAccount}
            onResend={() => {}}
            onBack={() => setView('login')}
          />
        )}

        {view === 'reset' && (
          <ResetPasswordForm
            t={t}
            maskedEmail={maskedEmail}
            onSubmit={handleResetSubmit}
            onBack={() => setView('forgot')}
            onResend={() => sendForgotPasswordCode(forgotAccount, i18n.language).catch(() => {})}
            submitting={resetSubmitting}
            submitError={resetError}
          />
        )}
      </div>
    </div>
  );
}

export default Login;
